// ─────────────────────────────────────────────────────────────────────────────
// GAME LOOP  –  ties physics / entities / renderer together
// ─────────────────────────────────────────────────────────────────────────────

import {
  PLATFORMS, SPIKES, ENEMIES, ZONE_CARDS, CHECKPOINTS,
  END_FLAG_X, GROUND_Y, WORLD_WIDTH, WORLD_HEIGHT, ASTRONAUT,
  getJetpackPos,
} from '../data/levelData';
import { createPlayer, stepPhysics, carryOnPlatform, nearPlatformTop, PhysicsBody } from './physics';
import {
  calcMovingOffsets,
  initEnemies, EnemyState, stepEnemies,
  playerHitsEnemy,
  Particle, spawnDeathParticles, spawnAmbientParticle, spawnJetpackFlame, stepParticles,
} from './entities';
import {
  drawBackground, drawPlatforms, drawSpikes, drawEnemies,
  drawParticles, drawPlayer, drawEndFlag, drawCheckpoints,
  drawHUD, drawToast, drawDeath, drawWin, drawControls,
  drawAstronaut, drawJetpackPickup,
} from './renderer';
import {
  getAxisX, consumeJump, jumpHeld, actionHeld, consumeAction,
  initInput, isTouchDevice,
} from './input';

// ─── Camera ──────────────────────────────────────────────────────────────────
const CAM_LEAD   = 0.35;
const CAM_SMOOTH = 6;

// ─── Toasts ──────────────────────────────────────────────────────────────────
type Toast = {
  title: string;
  lines: string[];
  age: number;
  life: number; // total seconds visible
};

const TOAST_LIFE = 4.2;
const TOAST_FADE_IN  = 0.25;
const TOAST_FADE_OUT = 0.55;

// ─── State machine ───────────────────────────────────────────────────────────
type GamePhase = 'playing' | 'dying' | 'win';

export type GameState = {
  phase:       GamePhase;
  player:      PhysicsBody;
  enemies:     EnemyState[];
  particles:   Particle[];
  camX:        number;
  time:        number;
  deathTimer:  number;
  winAlpha:    number;
  toast:       Toast | null;
  toastQueue:  Toast[];
  shownInfo:   Set<string>;
  shownCards:  Set<number>;
  deaths:      number;
  activeZone:  number;
  checkpointX: number;
  checkpointY: number;
  hasJetpack:  boolean;
  jetpackTaken: boolean;
  npcLine:     number; // -1 = not started, else index into ASTRONAUT.lines
  prevOffsets: Map<number, { ox: number; oy: number }>;
};

function spawnFloorY(): number { return GROUND_Y - 50; }

function makeState(): GameState {
  return {
    phase:        'playing',
    player:       createPlayer(80, spawnFloorY()),
    enemies:      initEnemies(ENEMIES),
    particles:    [],
    camX:         0,
    time:         0,
    deathTimer:   0,
    winAlpha:     0,
    toast:        null,
    toastQueue:   [],
    shownInfo:    new Set(),
    shownCards:   new Set(),
    deaths:       0,
    activeZone:   1,
    checkpointX:  80,
    checkpointY:  spawnFloorY(),
    hasJetpack:   false,
    jetpackTaken: false,
    npcLine:      -1,
    prevOffsets:  new Map(),
  };
}

function enqueueToast(gs: GameState, title: string, lines: string[]): void {
  const t: Toast = { title, lines, age: 0, life: TOAST_LIFE };
  if (!gs.toast) gs.toast = t;
  else gs.toastQueue.push(t);
}

function toastAlpha(t: Toast): number {
  if (t.age < TOAST_FADE_IN) return t.age / TOAST_FADE_IN;
  if (t.age > t.life - TOAST_FADE_OUT) {
    return Math.max(0, (t.life - t.age) / TOAST_FADE_OUT);
  }
  return 1;
}

function stepToasts(gs: GameState, dt: number): void {
  if (!gs.toast) {
    if (gs.toastQueue.length) gs.toast = gs.toastQueue.shift()!;
    return;
  }
  gs.toast.age += dt;
  if (gs.toast.age >= gs.toast.life) {
    gs.toast = gs.toastQueue.length ? gs.toastQueue.shift()! : null;
  }
}

// ─── Spike AABB ──────────────────────────────────────────────────────────────
function onSpike(body: PhysicsBody): boolean {
  for (const s of SPIKES) {
    if (
      body.x + body.w > s.x &&
      body.x < s.x + s.w &&
      body.y + body.h > s.y &&
      body.y + body.h < s.y + 30
    ) return true;
  }
  return false;
}

// ─── Checkpoint update ───────────────────────────────────────────────────────
function tryCheckpoint(gs: GameState): void {
  for (const cp of CHECKPOINTS) {
    if (gs.player.x >= cp.x && cp.zoneId > gs.activeZone) {
      gs.activeZone  = cp.zoneId;
      gs.checkpointX = cp.x + 60;
      gs.checkpointY = spawnFloorY();
    }
  }
}

// ─── Zone toasts (non-pausing) ───────────────────────────────────────────────
function tryZoneCard(gs: GameState): void {
  for (const card of ZONE_CARDS) {
    if (gs.shownCards.has(card.id)) continue;
    const band = card.triggerW ?? 120;
    if (gs.player.x + gs.player.w >= card.triggerX &&
        gs.player.x <= card.triggerX + band) {
      gs.shownCards.add(card.id);
      enqueueToast(gs, card.title, card.lines);
    }
  }
}

// ─── Platform tidbits ────────────────────────────────────────────────────────
function tryPlatformInfo(
  gs: GameState,
  offsets: Map<number, { ox: number; oy: number }>,
): void {
  for (let i = 0; i < PLATFORMS.length; i++) {
    const p = PLATFORMS[i];
    if (!p.infoId || !p.tidbit) continue;
    if (gs.shownInfo.has(p.infoId)) continue;
    const mo = offsets.get(i) ?? { ox: 0, oy: 0 };
    if (nearPlatformTop(gs.player, p, mo.ox, mo.oy, 56, 40)) {
      gs.shownInfo.add(p.infoId);
      const title = p.label ? `> ${p.label}` : '> DISCOVERY';
      // wrap tidbit into ~2 lines for toast width
      const words = p.tidbit.split(' ');
      const lines: string[] = [];
      let cur = '';
      for (const w of words) {
        if ((cur + ' ' + w).trim().length > 48) {
          lines.push(cur);
          cur = w;
        } else {
          cur = (cur + ' ' + w).trim();
        }
      }
      if (cur) lines.push(cur);
      enqueueToast(gs, title, lines);
    }
  }
}

// ─── NPC talk ────────────────────────────────────────────────────────────────
function nearNpc(body: PhysicsBody): boolean {
  const n = ASTRONAUT;
  const cx = n.x + n.w / 2;
  const cy = n.y + n.h / 2;
  const px = body.x + body.w / 2;
  const py = body.y + body.h / 2;
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy <= n.interactR * n.interactR;
}

function tryNpcTalk(gs: GameState, actionEdge: boolean): void {
  if (!actionEdge || !nearNpc(gs.player)) return;
  // Don't steal E while thrusting with jetpack far from NPC
  const next = gs.npcLine + 1;
  if (next >= ASTRONAUT.lines.length) {
    enqueueToast(gs, '> OLD ASTRONAUT', [
      'That\'s the briefing, cadet. Good luck out there.',
    ]);
    return;
  }
  gs.npcLine = next;
  const line = ASTRONAUT.lines[next];
  enqueueToast(gs, line.title, line.body);
}

// ─── Jetpack pickup ──────────────────────────────────────────────────────────
function tryJetpackPickup(gs: GameState): void {
  if (gs.jetpackTaken) return;
  const jp = getJetpackPos();
  const body = gs.player;
  const near =
    body.x + body.w > jp.x - 20 &&
    body.x < jp.x + 48 &&
    body.y + body.h > jp.y - 10 &&
    body.y < jp.y + 50;
  if (!near) return;
  gs.jetpackTaken = true;
  gs.hasJetpack = true;
  enqueueToast(gs, '> JETPACK ACQUIRED', [
    'Hold E (or Action) to thrust skyward.',
    'The sky is yours. Watch for drones.',
  ]);
}

// ─── Respawn ─────────────────────────────────────────────────────────────────
function respawn(gs: GameState): void {
  gs.player     = createPlayer(gs.checkpointX, gs.checkpointY);
  gs.phase      = 'playing';
  gs.deathTimer = 0;
}

function triggerDeath(gs: GameState): void {
  if (gs.phase === 'dying') return;
  gs.deaths++;
  gs.phase      = 'dying';
  gs.deathTimer = 0;
  spawnDeathParticles(gs.player.x + 14, gs.player.y + 24, gs.particles);
}

export type GameHooks = {
  onWin?: () => void;
  onRestart?: () => void;
};

export type GameHandle = {
  cleanup: () => void;
  restart: () => void;
};

// ═════════════════════════════════════════════════════════════════════════════
// PUBLIC: start the game loop
// ═════════════════════════════════════════════════════════════════════════════
export function startGame(canvas: HTMLCanvasElement, hooks: GameHooks = {}): GameHandle {
  const ctx          = canvas.getContext('2d')!;
  const cleanupInput = initInput();
  const gs           = makeState();
  const touchUI      = isTouchDevice();
  let winNotified    = false;

  const restart = () => {
    const fresh = makeState();
    Object.assign(gs, fresh);
    winNotified = false;
    hooks.onRestart?.();
  };

  const onRestartKey = (e: KeyboardEvent) => {
    if ((e.key === 'r' || e.key === 'R') && gs.phase === 'win') {
      restart();
    }
  };
  window.addEventListener('keydown', onRestartKey);

  let lastTs = 0;
  let rafId  = 0;

  function frame(ts: number): void {
    const dt  = Math.min((ts - lastTs) / 1000, 0.05);
    lastTs    = ts;
    gs.time  += dt;

    const W = canvas.width;
    const H = canvas.height;

    // Fit full world height into the viewport (fixes "too zoomed in")
    const scale = H / WORLD_HEIGHT;
    const viewW = W / scale;

    const movingOffsets = calcMovingOffsets(PLATFORMS, gs.time);

    // Carry player with moving platforms before physics
    carryOnPlatform(gs.player, gs.prevOffsets, movingOffsets);

    switch (gs.phase) {
      case 'playing': {
        const ax     = getAxisX();
        const doJump = consumeJump();
        const held   = jumpHeld();
        const actEdge = consumeAction();
        const thrusting = gs.hasJetpack && actionHeld() && !nearNpc(gs.player);

        // NPC talk takes priority when in range (edge); otherwise jetpack uses hold
        tryNpcTalk(gs, actEdge);
        tryJetpackPickup(gs);

        const result = stepPhysics(
          gs.player, dt, ax, doJump, held, PLATFORMS, movingOffsets, thrusting,
        );

        if (thrusting) {
          spawnJetpackFlame(
            gs.player.x + gs.player.w / 2,
            gs.player.y + gs.player.h,
            gs.particles,
          );
        }

        if (result === 'dead' || onSpike(gs.player)) {
          triggerDeath(gs);
          break;
        }

        stepEnemies(gs.enemies, dt, gs.time);
        for (const e of gs.enemies) {
          if (playerHitsEnemy(
            gs.player.x, gs.player.y, gs.player.w, gs.player.h, e,
          )) {
            triggerDeath(gs);
            break;
          }
        }

        tryCheckpoint(gs);
        tryZoneCard(gs);
        tryPlatformInfo(gs, movingOffsets);

        if (
          gs.player.x + gs.player.w >= END_FLAG_X &&
          !gs.shownCards.has(99)
        ) {
          gs.shownCards.add(99);
          gs.phase    = 'win';
          gs.winAlpha = 0;
          if (!winNotified) {
            winNotified = true;
            hooks.onWin?.();
          }
        }

        const targetCamX = gs.player.x - viewW * CAM_LEAD;
        gs.camX += (targetCamX - gs.camX) * Math.min(CAM_SMOOTH * dt, 1);
        gs.camX  = Math.max(0, Math.min(gs.camX, Math.max(0, WORLD_WIDTH - viewW)));

        if (Math.random() < dt * 4) spawnAmbientParticle(gs.camX, viewW, gs.particles);
        break;
      }

      case 'dying': {
        gs.deathTimer += dt;
        if (gs.deathTimer >= 1.8) respawn(gs);
        break;
      }

      case 'win': {
        gs.winAlpha = Math.min(gs.winAlpha + dt * 1.5, 1);
        break;
      }
    }

    gs.prevOffsets = movingOffsets;
    stepParticles(gs.particles, dt);
    stepToasts(gs, dt);

    // ── Draw (world space, scaled to fit height) ─────────────────────────────
    const camX = gs.camX;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    drawBackground(ctx, camX, viewW, WORLD_HEIGHT, gs.time);
    drawCheckpoints(ctx, CHECKPOINTS, gs.activeZone, camX, gs.time);
    drawPlatforms(ctx, PLATFORMS, movingOffsets, camX, viewW, gs.time);
    drawSpikes(ctx, SPIKES, camX);
    drawEndFlag(ctx, camX, gs.time);
    drawAstronaut(ctx, camX, gs.time, nearNpc(gs.player) && gs.phase === 'playing');
    if (!gs.jetpackTaken) {
      const jp = getJetpackPos();
      drawJetpackPickup(ctx, jp.x, jp.y, camX, gs.time);
    }
    drawEnemies(ctx, gs.enemies, camX, viewW, gs.time);
    drawParticles(ctx, gs.particles, camX);
    drawPlayer(
      ctx, gs.player, camX, gs.time,
      gs.phase === 'dying',
      gs.hasJetpack,
      gs.hasJetpack && actionHeld(),
    );

    // ── Screen-space overlays ────────────────────────────────────────────────
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const progress = Math.min(gs.player.x / (END_FLAG_X + 200), 1);
    drawHUD(ctx, W, gs.activeZone, gs.deaths, progress, gs.hasJetpack);
    drawControls(ctx, W, H, touchUI, gs.hasJetpack);

    if (gs.toast) {
      drawToast(ctx, W, H, {
        title: gs.toast.title,
        lines: gs.toast.lines,
        alpha: toastAlpha(gs.toast),
      });
    }

    if (gs.phase === 'dying') {
      const fadeIn  = Math.min(gs.deathTimer / 0.4, 1);
      const fadeOut = Math.max(0, 1 - (gs.deathTimer - 1.2) / 0.6);
      drawDeath(ctx, W, H, fadeIn * fadeOut);
    }

    if (gs.phase === 'win') {
      // Backdrop only; interactive links live in the React overlay
      drawWin(ctx, W, H, gs.winAlpha);
    }

    rafId = requestAnimationFrame(frame);
  }

  rafId = requestAnimationFrame((ts) => {
    lastTs = ts;
    rafId  = requestAnimationFrame(frame);
  });

  return {
    restart,
    cleanup: () => {
      cancelAnimationFrame(rafId);
      cleanupInput();
      window.removeEventListener('keydown', onRestartKey);
    },
  };
}
